import classnames from 'classnames';
import { type CSSProperties, type ReactNode, useState } from 'react';
import { type DropTargetMonitor, useDrop } from 'react-dnd';

import { type Clip } from '@air/api/types';

import { checkIfCanMoveAssets } from '~/components/Shared/Drag/DNDParentBoard/methods';
import { DragType, type DropData, type DropLocation, type GetAssetDragItem } from '~/components/Shared/Drag/dragTypes';
import { useAssetPermissionsCache } from '~/hooks/useAssetPermissionsCache';
import { selectedBoardsSelector, selectedClipsAssetIdsSelector } from '~/store/selectedItems/selectors';
import { canMergeAsset } from '~/utils/permissions/assetPermissions';
import { useAirStore } from '~/utils/ReduxUtils';

export type DNDParentAssetProps = {
  asset: Clip;
  children: ReactNode;
  dropLocation: DropLocation;
  style?: CSSProperties;
};

export const DNDParentAsset = ({ asset, children, dropLocation, style }: DNDParentAssetProps) => {
  const [canDrop, setCanDrop] = useState(false);
  const store = useAirStore();
  const selectedAssetsIds = selectedClipsAssetIdsSelector(store.getState());
  const selectedBoards = selectedBoardsSelector(store.getState());
  const { getAssetPermissions } = useAssetPermissionsCache();

  const checkIfCanDrop = (monitor: DropTargetMonitor<GetAssetDragItem>) => {
    // this comes from the `getItem` of DNDMovableToBoardItem
    const item = monitor.getItem();

    const permissions = getAssetPermissions(asset.assetId);

    if (!canMergeAsset(permissions)) {
      return false;
    }

    if ((item.type === 'asset' || item.type === 'file' || selectedAssetsIds.length) && !selectedBoards.length) {
      if (!item.assetId) {
        return false;
      }

      return checkIfCanMoveAssets([...selectedAssetsIds, item.assetId], asset.assetId);
    } else {
      return false;
    }
  };

  const [{ isOver }, drop] = useDrop({
    accept: [DragType.asset, DragType.file],
    drop: (): DropData => ({
      type: 'move',
      location: dropLocation,
      data: asset,
    }),
    hover: (_props, monitor) => {
      const canDropItem = checkIfCanDrop(monitor);

      if (canDropItem !== canDrop) {
        setCanDrop(canDropItem);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
    canDrop: (_props, monitor) => checkIfCanDrop(monitor),
  });

  return (
    <div
      data-draggable="true"
      className={classnames('group/droppable-item relative h-full', {
        'hovering-drop': isOver && canDrop,
      })}
      style={style}
      ref={drop}
    >
      {children}
    </div>
  );
};
